import React, { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ColorRing } from "react-loader-spinner";
import axios from "../../axios";
import { AUTH_API_URL } from "../../constants/api";
import { verifyGuestViaSocket } from "../../chat/verifyGuestSocket";
import shieldSecurity from "../../assets/images/verify/shield-security.png";
import shieldTick from "../../assets/images/verify/shield-tick.png";
import closeCircle from "../../assets/images/verify/close-circle.png";
import "./GuestVerify.scss";

const STATES = {
  IDLE: "idle",
  PENDING: "pending",
  SUCCESS: "success",
  ERROR: "error",
};

const DEFAULT_ERROR = "Verification failed.";

/** Coerce API `message` (string | array | object) into a safe React string. */
const toErrorMessage = (value, fallback = DEFAULT_ERROR) => {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => toErrorMessage(item, ""))
      .filter(Boolean);
    return parts.length ? parts.join(" ") : fallback;
  }
  if (value && typeof value === "object") {
    if (typeof value.message === "string" && value.message.trim()) {
      return value.message.trim();
    }
    const parts = Object.values(value)
      .flatMap((item) => (Array.isArray(item) ? item : [item]))
      .map((item) => (typeof item === "string" ? item.trim() : ""))
      .filter(Boolean);
    if (parts.length) return parts.join(" ");
  }
  return fallback;
};

const isApiFailure = (payload) => {
  if (!payload || typeof payload !== "object") return false;
  const { success } = payload;
  return success === false || success === 0 || success === "false";
};

const GuestVerify = () => {
  const [searchParams] = useSearchParams();
  const verificationId = searchParams.get("id");
  const code = searchParams.get("code");

  const [status, setStatus] = useState(
    verificationId && code ? STATES.IDLE : STATES.ERROR
  );
  const [errorMessage, setErrorMessage] = useState(
    verificationId && code
      ? ""
      : "This verification link is missing required information."
  );
  const [canRetry, setCanRetry] = useState(false);
  const [showFailIcon, setShowFailIcon] = useState(!(verificationId && code));

  const showBackendFailure = useCallback((message) => {
    setErrorMessage(toErrorMessage(message));
    setCanRetry(false);
    setShowFailIcon(true);
    setStatus(STATES.ERROR);
  }, []);

  const showRetryableFailure = useCallback((message) => {
    setErrorMessage(toErrorMessage(message, "Something went wrong. Please try again."));
    setCanRetry(true);
    setShowFailIcon(false);
    setStatus(STATES.ERROR);
  }, []);

  const handleVerify = useCallback(async () => {
    if (!verificationId || !code) {
      showBackendFailure(
        "This verification link is missing required information."
      );
      return;
    }

    setStatus(STATES.PENDING);
    setErrorMessage("");
    setCanRetry(false);
    setShowFailIcon(false);

    try {
      // Route exists on /api/v1 (not /api/guest/v1). Use absolute URL so axios base is ignored.
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { data } = await axios.post(
        `${AUTH_API_URL}/auth/guest-verification/complete`,
        {
          verification_id: verificationId,
          code,
          timezone,
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      // e.g. { success:false, message:"Selected unit was not found.", data:[] }
      if (isApiFailure(data)) {
        showBackendFailure(data?.message);
        return;
      }

      // `data: []` on failure is truthy — never treat an array as the token payload.
      const payload = data?.data;
      const socketToken =
        payload && !Array.isArray(payload) ? payload.socket_token : null;

      if (!socketToken) {
        showRetryableFailure(
          data?.message ||
            "Verification succeeded but no socket token was returned."
        );
        return;
      }

      await verifyGuestViaSocket(socketToken);
      setStatus(STATES.SUCCESS);
    } catch (err) {
      const responseData = err?.response?.data;

      if (isApiFailure(responseData)) {
        showBackendFailure(responseData?.message);
        return;
      }

      showRetryableFailure(
        responseData?.message ||
          err?.message ||
          "Something went wrong. Please try again."
      );
    }
  }, [verificationId, code, showBackendFailure, showRetryableFailure]);

  return (
    <div className="guest-verify">
      <div className="guest-verify__card">
        {status === STATES.IDLE && (
          <>
            <div className="guest-verify__icon-wrap is-request">
              <img
                className="guest-verify__icon"
                src={shieldSecurity}
                alt=""
              />
            </div>
            <h1 className="guest-verify__title">
              Identity Verification Requested
            </h1>
            <p className="guest-verify__body">
              Your host has asked you to verify your identity before accessing
              protected information.
            </p>
            <button
              type="button"
              className="guest-verify__btn"
              onClick={handleVerify}
            >
              Verify my Identity
            </button>
          </>
        )}

        {status === STATES.PENDING && (
          <div className="guest-verify__pending">
            <ColorRing
              visible
              height="72"
              width="72"
              ariaLabel="verifying"
              colors={["#1a237e", "#3949ab", "#5c6bc0", "#3949ab", "#1a237e"]}
            />
            <p className="guest-verify__pending-text">
              Verifying your identity…
            </p>
          </div>
        )}

        {status === STATES.SUCCESS && (
          <>
            <div className="guest-verify__icon-wrap is-success">
              <img className="guest-verify__icon" src={shieldTick} alt="" />
            </div>
            <h1 className="guest-verify__title">Identity Verified!</h1>
            <p className="guest-verify__body">
              Your verification is complete! You can now return to the main page
              and continue chatting with the host.
            </p>
            <p className="guest-verify__hint">You can close this tab.</p>
          </>
        )}

        {status === STATES.ERROR && (
          <>
            <div
              className={`guest-verify__icon-wrap ${
                showFailIcon ? "is-fail" : "is-request"
              }`}
            >
              <img
                className="guest-verify__icon"
                src={showFailIcon ? closeCircle : shieldSecurity}
                alt=""
              />
            </div>
            <h1 className="guest-verify__title">Verification Failed</h1>
            <p className="guest-verify__body">
              {typeof errorMessage === "string" && errorMessage
                ? errorMessage
                : DEFAULT_ERROR}
            </p>
            {canRetry ? (
              <button
                type="button"
                className="guest-verify__btn"
                onClick={handleVerify}
              >
                Try again
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default GuestVerify;
